import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import { type CalendarFocusMonth, clampCalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import Group from 'components/utils/Group'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { mapFlexibleDatesToTypeaheadFormat } from 'lib/search/searchUtils'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { addDays, addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import type moment from 'moment'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import FlexibleDatePicker from './FlexibleDatePicker'
import FormattedSearchDate from './FormattedSearchDate'
import SearchDateToggle from './SearchDateToggle'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef' | 'open'> {
  onDatesApply: () => void
  onDatesDecline: () => void
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  selectAnytime?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  durationOptionsCustom?: Array<DurationOption>
  isSpecificSearchEnabled?: boolean
  showFlexibleYearSelector?: boolean
  closeMenu?: () => void
  disableFlexible?: boolean
  /** Called when user manually selects dates in the calendar */
  onUserDatesChanged?: (dates: App.TypeaheadExtractedTravelDates) => void
  /**
   * Month the calendar should open on when no check-in date has been picked yet.
   * Only moves the viewport — it never selects a date.
   */
  focusMonth?: CalendarFocusMonth
}

function SearchDateDropDown(props: Props) {
  const {
    anchorRef,
    triggerRef,
    open,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    durationOptionsCustom,
    isSpecificSearchEnabled = true,
    showFlexibleYearSelector,
    onUserDatesChanged,
    closeMenu,
    disableFlexible,
    focusMonth,
  } = props
  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const intl = useIntl()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const today = useMemo(() => beginningOfToday(), [])

  const localInputRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  // show flexible search if the search option is flexible dates or if the search option is anytime with no specific search enabled
  const shouldShowFlexibleSearch = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ||
    (!isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  const shouldShowSpecific = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    (isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  useEffect(() => {
    if (localInputRef?.current) {
      localInputRef.current.focus()
    }
  }, [localInputRef])

  const maxDate = useMemo(
    () =>
      checkinDate && !checkoutDate ?
          addDays(checkinDate.toDate(), 31) :
          addMonths(today, 36),
    [checkinDate, checkoutDate, today],
  )

  // An existing check-in date already drives the calendar, so the hint only
  // applies before the traveller has picked anything.
  const calendarFocusDate = useMemo(
    () => checkinDate ? undefined : clampCalendarFocusMonth(focusMonth, today, maxDate),
    [checkinDate, focusMonth, maxDate, today],
  )

  const handleDatesChanged = useCallback(
    // changed types here to match the DualDateRangePicker props
    (dates: { startDate: moment.Moment, endDate: moment.Moment | undefined }) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKIN_DATE,
        date: dates.startDate,
      })
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
        date: dates.endDate,
      })

      if (dates.startDate && dates.endDate) {
        selectSpecificDates?.()
        onUserDatesChanged?.({
          type: 'specific',
          checkIn: dates.startDate.format(ISO_DATE_FORMAT),
          checkOut: dates.endDate.format(ISO_DATE_FORMAT),
        })
      }
    },
    [globalSearchDispatch, selectSpecificDates, onUserDatesChanged],
  )

  const handleFlexibleNightsChanged = useCallback(
    (newFlexibleNights: FLEXIBLE_DURATION_RANGE) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: newFlexibleNights,
      })
      if (newFlexibleNights && flexibleMonths) {
        selectFlexibleDates?.()
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonths, newFlexibleNights))
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleMonths],
  )

  const handleFlexibleMonthOptionsChanged = useCallback(
    (flexibleMonthOptions: string) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
        flexibleMonths: flexibleMonthOptions,
      })
      if (flexibleMonthOptions.length > 0) {
        selectFlexibleDates?.()
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
        const currentFlexibleNights = flexibleNights || DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonthOptions, currentFlexibleNights))
      } else {
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS,
        })
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleNights],
  )

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      selectAnytimeDate?.(true)
    }
  }, [selectAnytimeDate])

  const selectAnytimeDateNoApply = useCallback(() => {
    selectAnytimeDate?.(false)
    onUserDatesChanged?.({ type: 'anytime' })
  }, [selectAnytimeDate, onUserDatesChanged])

  const onSelectSpecificDates = useCallback(() => {
    selectSpecificDates?.()
  }, [selectSpecificDates])

  const hasDates = !!(checkinDate && checkoutDate)
  const disableSearchButton = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && !hasDates

  return <DropdownSheet
    data-testid="search-date-dropdown"
    size="fill-anchor"
    open={open}
    onClose={closeMenu}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    bodyRef={bodyRef}
    primaryActionProps={{
      'data-testid': 'search-apply-button',
      children: intl.formatMessage({
        id: 'dUn1eYBZXOHncdrqjYwc',
        defaultMessage: 'Apply',
        description: 'Button text to apply selected dates in search date picker',
      }),
      disabled: disableSearchButton,
      onClick: onDatesApply,
    }}
    secondaryActionProps={{
      'data-testid': 'search-nodates-button',
      kind: 'tertiary',
      children: intl.formatMessage({
        id: 'sF9A_o8aEaK3HKqs6QYk',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel date selection in search date picker',
      }),
      onClick: onDatesDecline,
    }}
    footerStart={<Group direction="horizontal" horizontalAlign="end"><FormattedSearchDate /></Group>}
  >
    <VerticalSpacer gap={32} onKeyDown={onKeyDown}>
      <Group
        direction="horizontal"
        horizontalAlign="center"
      >
        <SearchDateToggle
          onSelectAnytime={selectAnytimeDateNoApply}
          onSelectSpecific={onSelectSpecificDates}
          anytimeDateSelected={!!anytimeDateSelected}
          onSelectFlexible={selectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          isSpecificSearchEnabled={isSpecificSearchEnabled}
          disableFlexible={disableFlexible}
        />
      </Group>
      <Group direction="vertical" horizontalAlign="center">
        {shouldShowFlexibleSearch && (
          <FlexibleDatePicker
            onMonthChange={handleFlexibleMonthOptionsChanged}
            onDurationChange={handleFlexibleNightsChanged}
            previouslySelectedDuration={flexibleNights}
            previouslySelectedMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
            durationOptionsCustom={durationOptionsCustom}
            showYearSelector={showFlexibleYearSelector}
          />
        )}
        {shouldShowSpecific && isMobileScreen && (
          <VerticalDateRangePicker
            onDatesChange={handleDatesChanged}
            startDate={checkinDate}
            endDate={checkoutDate}
            min={today}
            max={maxDate}
            contentRef={bodyRef}
            focusMonth={calendarFocusDate}
          />
        )}
        {shouldShowSpecific && !isMobileScreen && (
          <DualDateRangePicker
            onDatesChange={handleDatesChanged}
            startDate={checkinDate}
            endDate={checkoutDate}
            minDate={today}
            maxDate={maxDate}
            focusMonth={calendarFocusDate}
          />
        )}
      </Group>
    </VerticalSpacer>
  </DropdownSheet>
}

export default SearchDateDropDown
